import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, Trash2, ArrowRightLeft, ListOrdered, Settings2, Eye, ExternalLink, ArrowDownCircle, ArrowUpCircle, ShieldCheck, RefreshCcw, AlertTriangle, CheckCircle2 } from "lucide-react";
import { ProductoCombobox } from "@/components/ProductoCombobox";

export default function Inventory() {
  return (
    <div className="space-y-6" data-testid="inventory-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#5c7368] mb-1">Bodega</div>
        <h1 className="text-3xl font-bold tracking-tight text-[#15241d]" style={{ fontFamily: "'Manrope', sans-serif" }}>Movimientos de inventario</h1>
        <p className="text-sm text-[#4a5c53] mt-1">Kardex, transferencias entre bodegas y ajustes de inventario.</p>
      </div>
      <Tabs defaultValue="movimientos">
        <TabsList className="bg-white border border-[#d1dbd6]">
          <TabsTrigger value="movimientos"><ListOrdered size={14} className="mr-2" />Movimientos</TabsTrigger>
          <TabsTrigger value="transferencias"><ArrowRightLeft size={14} className="mr-2" />Transferencias</TabsTrigger>
          <TabsTrigger value="ajustes"><Settings2 size={14} className="mr-2" />Ajustes / Ingresos</TabsTrigger>
          <TabsTrigger value="auditoria" data-testid="tab-auditoria-stock"><ShieldCheck size={14} className="mr-2" />Auditoría stock</TabsTrigger>
        </TabsList>
        <TabsContent value="movimientos"><Movimientos /></TabsContent>
        <TabsContent value="transferencias"><Transferencias /></TabsContent>
        <TabsContent value="ajustes"><Ajustes /></TabsContent>
        <TabsContent value="auditoria"><AuditoriaStock /></TabsContent>
      </Tabs>
    </div>
  );
}

function Movimientos() {
  const [items, setItems] = useState([]);
  const [bodegas, setBodegas] = useState([]);
  const [facturas, setFacturas] = useState([]);
  const [detalleOpen, setDetalleOpen] = useState(null);
  const load = async () => {
    const [m, b] = await Promise.all([
      api.get("/inventory/movimientos"),
      api.get("/masters/catalog/bodegas"),
    ]);
    setItems(m.data); setBodegas(b.data);
    // Facturas para resolver redirección por numero (lazy: solo si hay movimientos referencing facturas)
    try {
      const { data: f } = await api.get("/invoicing/facturas");
      setFacturas(f);
    } catch (_) { /* sin permiso, ignorar */ }
  };
  useEffect(() => { load().catch(e => toast.error(formatApiError(e))); }, []);

  const bodMap = Object.fromEntries(bodegas.map(b => [b.id, b.nombre]));
  const facturaIdByNumero = useMemo(() => Object.fromEntries(facturas.map(f => [f.numero, f.id])), [facturas]);

  const eliminarMov = async (m) => {
    if (!m.ajuste_id) {
      return toast.error("Sólo se pueden eliminar movimientos de ajuste manual. Para reversar transferencias o compras, use el módulo correspondiente.");
    }
    if (!window.confirm(`¿Eliminar el ajuste ${m.documento_ref}? El stock se revertirá automáticamente.`)) return;
    try {
      await api.delete(`/inventory/ajustes/${m.ajuste_id}`);
      toast.success("Ajuste eliminado · stock revertido");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const tipoLabel = (t) => {
    if (!t) return "—";
    const map = {
      transferencia: "Transferencia", ingreso_compra: "Ingreso por compra",
      ingreso_ingreso: "Ingreso manual", ingreso_regularizacion: "Ingreso · regularización",
      egreso_merma: "Egreso · merma", egreso_dano: "Egreso · daño",
      egreso_vencimiento: "Egreso · vencimiento", egreso_regularizacion: "Egreso · regularización",
      salida_venta: "Salida por venta", devolucion_venta: "Devolución de venta",
      ingreso: "Ingreso", egreso: "Egreso",
    };
    return map[t] || t.replace(/_/g, " ");
  };

  return (
    <div className="mt-4 rounded-md border border-[#d1dbd6] bg-white overflow-hidden">
      <table className="erp-table">
        <thead><tr><th>Fecha</th><th>Tipo</th><th>Producto</th><th>Lote</th><th>Origen</th><th>Destino</th><th className="text-right">Cantidad</th><th>Documento</th><th className="w-32"></th></tr></thead>
        <tbody>
          {items.map(m => {
            const factId = m.documento_ref && facturaIdByNumero[m.documento_ref];
            const isAjuste = !!m.ajuste_id;
            const isCompra = m.tipo === "ingreso_compra";
            return (
              <tr key={m.id} data-testid={`mov-row-${m.id}`}>
                <td className="text-xs">{m.fecha}</td>
                <td><Badge variant="outline" className="capitalize text-[10px]">{tipoLabel(m.tipo)}</Badge></td>
                <td className="font-medium">{m.producto_nombre || m.producto_comercial_id?.slice(0,8)}</td>
                <td className="font-mono text-xs">{m.numero_lote}</td>
                <td>{bodMap[m.bodega_origen_id] || "—"}</td>
                <td>{bodMap[m.bodega_destino_id] || "—"}</td>
                <td className={`text-right tabular-nums font-semibold ${(m.cantidad ?? 0) < 0 ? "text-[#7a2514]" : "text-[#1f6b42]"}`}>{m.cantidad}</td>
                <td className="font-mono text-xs">{m.documento_ref || "—"}</td>
                <td>
                  <div className="flex gap-1 justify-end">
                    {factId && (
                      <Link to={`/facturacion?f=${factId}`}>
                        <Button size="sm" variant="outline" title="Ver factura origen" data-testid={`mov-ver-fact-${m.id}`}>
                          <ExternalLink size={14} />
                        </Button>
                      </Link>
                    )}
                    {isCompra && (
                      <Link to="/compras">
                        <Button size="sm" variant="outline" title="Ver módulo de compras" data-testid={`mov-ver-compra-${m.id}`}>
                          <ExternalLink size={14} />
                        </Button>
                      </Link>
                    )}
                    {isAjuste && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => setDetalleOpen(m)} title="Ver motivo y observaciones" data-testid={`mov-ver-${m.id}`}>
                          <Eye size={14} />
                        </Button>
                        <Button size="sm" variant="outline" className="text-[#b53a25] hover:bg-[#fdecea]" onClick={() => eliminarMov(m)} title="Eliminar ajuste y revertir stock" data-testid={`mov-del-${m.id}`}>
                          <Trash2 size={14} />
                        </Button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
          {items.length === 0 && <tr><td colSpan={9} className="text-center py-10 text-[#8ba397]">Sin movimientos registrados</td></tr>}
        </tbody>
      </table>

      <MovimientoDetalleDialog mov={detalleOpen} onClose={() => setDetalleOpen(null)} onSaved={load} />
    </div>
  );
}

function MovimientoDetalleDialog({ mov, onClose, onSaved }) {
  const [motivo, setMotivo] = useState("");
  const [obs, setObs] = useState("");
  useEffect(() => {
    if (mov) { setMotivo(mov.motivo || ""); setObs(mov.observaciones || ""); }
  }, [mov]);
  const guardar = async () => {
    if (!mov?.ajuste_id) return;
    try {
      await api.put(`/inventory/ajustes/${mov.ajuste_id}`, { motivo, observaciones: obs });
      toast.success("Notas actualizadas");
      onSaved(); onClose();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Dialog open={!!mov} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-xl" data-testid="mov-detalle-dialog">
        <DialogHeader><DialogTitle>Detalle de ajuste {mov?.documento_ref}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><span className="text-[#5c7368] text-xs uppercase tracking-wider">Tipo</span><div className="font-medium capitalize">{mov?.tipo?.replace(/_/g, " ")}</div></div>
            <div><span className="text-[#5c7368] text-xs uppercase tracking-wider">Cantidad</span><div className={`font-semibold tabular-nums ${(mov?.cantidad ?? 0) < 0 ? "text-[#7a2514]" : "text-[#1f6b42]"}`}>{mov?.cantidad}</div></div>
            <div><span className="text-[#5c7368] text-xs uppercase tracking-wider">Lote</span><div className="font-mono text-xs">{mov?.numero_lote}</div></div>
            <div><span className="text-[#5c7368] text-xs uppercase tracking-wider">Fecha</span><div>{mov?.fecha}</div></div>
          </div>
          <div><Label>Motivo</Label><Input value={motivo} onChange={(e) => setMotivo(e.target.value)} data-testid="mov-edit-motivo" /></div>
          <div><Label>Observaciones</Label><Textarea value={obs} onChange={(e) => setObs(e.target.value)} rows={3} data-testid="mov-edit-obs" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cerrar</Button>
          <Button onClick={guardar} className="bg-[#205c45]" data-testid="mov-edit-save">Guardar notas</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Transferencias() {
  const [items, setItems] = useState([]);
  const [bodegas, setBodegas] = useState([]);
  const [lotes, setLotes] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(empty());
  function empty() { return { bodega_origen_id: "", bodega_destino_id: "", fecha: new Date().toISOString().slice(0,10), observaciones: "", lineas: [] }; }

  const load = async () => {
    const [t, b, l] = await Promise.all([
      api.get("/inventory/transferencias"),
      api.get("/masters/catalog/bodegas"),
      api.get("/masters/lotes"),
    ]);
    setItems(t.data); setBodegas(b.data); setLotes(l.data);
  };
  useEffect(() => { load().catch(e => toast.error(formatApiError(e))); }, []);

  const lotesEnOrigen = lotes.filter(l => l.bodega_id === form.bodega_origen_id && l.cantidad_disponible > 0);
  const addLinea = () => setForm(f => ({...f, lineas: [...f.lineas, { lote_id: "", producto_comercial_id: "", producto_nombre: "", numero_lote: "", cantidad: 1 }]}));
  const updateLinea = (i, field, val) => setForm(f => {
    const nl = [...f.lineas]; nl[i] = {...nl[i], [field]: val};
    if (field === "lote_id") {
      const lote = lotes.find(x => x.id === val);
      if (lote) { nl[i].producto_comercial_id = lote.producto_comercial_id; nl[i].numero_lote = lote.numero_lote; }
    }
    return {...f, lineas: nl};
  });

  const save = async () => {
    try { await api.post("/inventory/transferencias", form); toast.success("Transferencia confirmada"); setDialogOpen(false); setForm(empty()); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const bodMap = Object.fromEntries(bodegas.map(b => [b.id, b.nombre]));

  return (
    <div className="mt-4 space-y-4">
      <div className="flex justify-end"><Button onClick={() => { setForm(empty()); setDialogOpen(true); }} className="bg-[#205c45]" data-testid="create-transfer-btn"><Plus size={16} className="mr-2" />Nueva transferencia</Button></div>
      <div className="rounded-md border border-[#d1dbd6] bg-white overflow-hidden">
        <table className="erp-table">
          <thead><tr><th>Número</th><th>Fecha</th><th>Origen</th><th>Destino</th><th># Items</th><th>Estado</th></tr></thead>
          <tbody>
            {items.map(t => (
              <tr key={t.id}>
                <td className="font-mono text-xs font-semibold">{t.numero}</td>
                <td>{t.fecha}</td>
                <td>{bodMap[t.bodega_origen_id]}</td>
                <td>{bodMap[t.bodega_destino_id]}</td>
                <td>{t.lineas?.length || 0}</td>
                <td><Badge className="fefo-safe border capitalize">{t.estado}</Badge></td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-[#8ba397]">Sin transferencias</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Nueva transferencia entre bodegas</DialogTitle></DialogHeader>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Bodega origen</Label>
              <Select value={form.bodega_origen_id} onValueChange={(v) => setForm({...form, bodega_origen_id: v, lineas: []})}>
                <SelectTrigger data-testid="transfer-origen"><SelectValue placeholder="..." /></SelectTrigger>
                <SelectContent>{bodegas.map(b => <SelectItem key={b.id} value={b.id}>{b.nombre}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Bodega destino</Label>
              <Select value={form.bodega_destino_id} onValueChange={(v) => setForm({...form, bodega_destino_id: v})}>
                <SelectTrigger data-testid="transfer-destino"><SelectValue placeholder="..." /></SelectTrigger>
                <SelectContent>{bodegas.filter(b => b.id !== form.bodega_origen_id).map(b => <SelectItem key={b.id} value={b.id}>{b.nombre}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Fecha</Label><Input type="date" value={form.fecha} onChange={(e) => setForm({...form, fecha: e.target.value})} /></div>
          </div>
          <div className="border border-[#d1dbd6] rounded-md mt-4">
            <div className="flex items-center justify-between px-3 py-2 bg-[#f7f9f8] border-b">
              <div className="text-xs uppercase tracking-wider font-semibold text-[#5c7368]">Lotes a transferir</div>
              <Button size="sm" variant="outline" onClick={addLinea} disabled={!form.bodega_origen_id}><Plus size={14} className="mr-1" />Agregar</Button>
            </div>
            <table className="erp-table">
              <thead><tr><th>Lote</th><th className="w-24">Cantidad</th><th className="w-10"></th></tr></thead>
              <tbody>
                {form.lineas.map((l, i) => (
                  <tr key={i}>
                    <td>
                      <Select value={l.lote_id} onValueChange={(v) => updateLinea(i, "lote_id", v)}>
                        <SelectTrigger className="h-9"><SelectValue placeholder="..." /></SelectTrigger>
                        <SelectContent>{lotesEnOrigen.map(lt => <SelectItem key={lt.id} value={lt.id}>Lote {lt.numero_lote} · disponible: {lt.cantidad_disponible}</SelectItem>)}</SelectContent>
                      </Select>
                    </td>
                    <td><Input className="h-9" type="number" value={l.cantidad} onChange={(e) => updateLinea(i, "cantidad", parseInt(e.target.value)||0)} /></td>
                    <td><Button size="icon" variant="ghost" onClick={() => setForm(f => ({...f, lineas: f.lineas.filter((_, x) => x !== i)}))}><Trash2 size={14} /></Button></td>
                  </tr>
                ))}
                {form.lineas.length === 0 && <tr><td colSpan={3} className="text-center py-4 text-[#8ba397] text-sm">{form.bodega_origen_id ? "Agregue lotes a transferir" : "Seleccione bodega origen"}</td></tr>}
              </tbody>
            </table>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button><Button onClick={save} className="bg-[#205c45]" data-testid="save-transfer-btn">Confirmar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Ajustes() {
  const [items, setItems] = useState([]);
  const [productos, setProductos] = useState([]);
  const [lotes, setLotes] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(empty());
  function empty() {
    return { producto_comercial_id: "", lote_id: "", direccion: "egreso", tipo: "merma", cantidad: 0, motivo: "", observaciones: "" };
  }

  const load = async () => {
    const [a, p, l] = await Promise.all([
      api.get("/inventory/ajustes"),
      api.get("/masters/productos-comerciales"),
      api.get("/masters/lotes"),
    ]);
    setItems(a.data); setProductos(p.data); setLotes(l.data);
  };
  useEffect(() => { load().catch(e => toast.error(formatApiError(e))); }, []);

  const lotesDelProducto = useMemo(() => (
    form.producto_comercial_id
      ? lotes.filter(l => l.producto_comercial_id === form.producto_comercial_id && l.activo !== false)
            .sort((a, b) => (a.fecha_vencimiento || "").localeCompare(b.fecha_vencimiento || ""))
      : []
  ), [lotes, form.producto_comercial_id]);

  const TIPOS_INGRESO = [
    { value: "ingreso", label: "Ingreso manual (encontrado / recuperado)" },
    { value: "regularizacion", label: "Regularización · faltante por contar" },
  ];
  const TIPOS_EGRESO = [
    { value: "merma", label: "Merma" },
    { value: "dano", label: "Daño" },
    { value: "vencimiento", label: "Vencimiento" },
    { value: "regularizacion", label: "Regularización · sobrante por contar" },
  ];
  const tiposActivos = form.direccion === "ingreso" ? TIPOS_INGRESO : TIPOS_EGRESO;

  const save = async () => {
    if (!form.producto_comercial_id) return toast.error("Seleccione un producto");
    if (!form.lote_id) return toast.error("Seleccione un lote del producto");
    if (!form.cantidad || form.cantidad <= 0) return toast.error("Ingrese una cantidad válida");
    if (!form.motivo) return toast.error("Indique el motivo");
    try {
      const { producto_comercial_id, ...payload } = form;
      await api.post("/inventory/ajustes", payload);
      toast.success(`${form.direccion === "ingreso" ? "Ingreso" : "Egreso"} registrado`);
      setDialogOpen(false); setForm(empty()); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="mt-4 space-y-4">
      <div className="flex justify-end"><Button onClick={() => { setForm(empty()); setDialogOpen(true); }} className="bg-[#205c45]" data-testid="create-ajuste-btn"><Plus size={16} className="mr-2" />Nuevo ajuste</Button></div>
      <div className="rounded-md border border-[#d1dbd6] bg-white overflow-hidden">
        <table className="erp-table">
          <thead><tr><th>Número</th><th>Lote</th><th>Dirección</th><th>Tipo</th><th className="text-right">Cantidad</th><th>Motivo</th></tr></thead>
          <tbody>
            {items.map(a => {
              const isIngreso = a.direccion === "ingreso";
              return (
                <tr key={a.id}>
                  <td className="font-mono text-xs">{a.numero}</td>
                  <td className="font-mono text-xs">{a.numero_lote}</td>
                  <td>
                    {isIngreso
                      ? <Badge className="bg-[#def5e6] text-[#1f6b42] border-[#9dd4ae] border"><ArrowUpCircle size={12} className="mr-1 inline" />Ingreso</Badge>
                      : <Badge className="bg-[#f5d6cf] text-[#7a2514] border-[#d99584] border"><ArrowDownCircle size={12} className="mr-1 inline" />Egreso</Badge>}
                  </td>
                  <td><Badge className="fefo-critico border capitalize">{a.tipo}</Badge></td>
                  <td className={`text-right tabular-nums font-semibold ${isIngreso ? "text-[#1f6b42]" : "text-[#7a2514]"}`}>{isIngreso ? "+" : "-"}{a.cantidad}</td>
                  <td>{a.motivo}</td>
                </tr>
              );
            })}
            {items.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-[#8ba397]">Sin ajustes</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>Ajuste de inventario · ingreso o egreso</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Dirección del ajuste</Label>
              <div className="flex gap-2 mt-1">
                <button type="button" onClick={() => setForm({ ...form, direccion: "ingreso", tipo: "ingreso" })} className={`flex-1 px-3 py-2 rounded border text-sm font-medium ${form.direccion === "ingreso" ? "bg-[#def5e6] border-[#9dd4ae] text-[#1f6b42]" : "bg-white border-[#d1dbd6] text-[#5c7368]"}`} data-testid="ajuste-dir-ingreso">
                  <ArrowUpCircle size={14} className="inline mr-1" /> Ingreso (sumar al stock)
                </button>
                <button type="button" onClick={() => setForm({ ...form, direccion: "egreso", tipo: "merma" })} className={`flex-1 px-3 py-2 rounded border text-sm font-medium ${form.direccion === "egreso" ? "bg-[#f5d6cf] border-[#d99584] text-[#7a2514]" : "bg-white border-[#d1dbd6] text-[#5c7368]"}`} data-testid="ajuste-dir-egreso">
                  <ArrowDownCircle size={14} className="inline mr-1" /> Egreso (descontar)
                </button>
              </div>
            </div>
            <div>
              <Label>Producto</Label>
              <ProductoCombobox
                value={form.producto_comercial_id}
                onChange={(v) => setForm({ ...form, producto_comercial_id: v, lote_id: "" })}
                productos={productos}
                placeholder="Buscar producto…"
                testId="ajuste-prod"
              />
            </div>
            <div>
              <Label>Lote {form.producto_comercial_id && <span className="text-xs text-[#8ba397]">({lotesDelProducto.length} disponible{lotesDelProducto.length === 1 ? "" : "s"})</span>}</Label>
              <Select value={form.lote_id} onValueChange={(v) => setForm({ ...form, lote_id: v })} disabled={!form.producto_comercial_id}>
                <SelectTrigger data-testid="ajuste-lote"><SelectValue placeholder={form.producto_comercial_id ? "Seleccione lote..." : "Primero elija un producto"} /></SelectTrigger>
                <SelectContent>
                  {lotesDelProducto.map(l => (
                    <SelectItem key={l.id} value={l.id}>
                      Lote {l.numero_lote} · disp: {l.cantidad_disponible}{l.fecha_vencimiento ? ` · vence ${l.fecha_vencimiento}` : ""}
                    </SelectItem>
                  ))}
                  {lotesDelProducto.length === 0 && form.producto_comercial_id && (
                    <div className="px-3 py-2 text-xs text-[#8ba397] italic">Este producto no tiene lotes activos</div>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Tipo / motivo del {form.direccion}</Label>
              <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {tiposActivos.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Cantidad</Label><Input type="number" min="1" value={form.cantidad} onChange={(e) => setForm({...form, cantidad: parseInt(e.target.value)||0})} data-testid="ajuste-cantidad" /></div>
            <div><Label>Motivo (texto corto)</Label><Input value={form.motivo} onChange={(e) => setForm({...form, motivo: e.target.value})} placeholder={form.direccion === "ingreso" ? "Ej: Producto encontrado en bodega 2" : "Ej: Frasco quebrado al manipular"} data-testid="ajuste-motivo" /></div>
            <div><Label>Observaciones (opcional)</Label><Textarea value={form.observaciones} onChange={(e) => setForm({...form, observaciones: e.target.value})} rows={2} data-testid="ajuste-obs" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button><Button onClick={save} className="bg-[#205c45]" data-testid="save-ajuste-btn">Registrar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ITER195 (Feb 2026): Auditoría de stock por lote — reconcilia
// `cantidad_disponible` contra la suma de movimientos + cantidad_inicial.
// Útil cuando el usuario sospecha que un lote quedó con stock inconsistente
// (ej. tras un split FEFO o edición de factura anterior).
function AuditoriaStock() {
  const [loteQuery, setLoteQuery] = useState("");
  const [loteId, setLoteId] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [aplicando, setAplicando] = useState(false);
  const [inconsistentes, setInconsistentes] = useState(null);
  const [scanning, setScanning] = useState(false);

  const buscarLote = async () => {
    if (!loteQuery.trim()) return toast.error("Ingresa un número de lote");
    setLoading(true);
    setResult(null);
    try {
      // Primero intentar por numero_lote
      const r = await api.get(`/masters/lotes?q=${encodeURIComponent(loteQuery.trim())}`).catch(() => ({ data: [] }));
      const lotes = Array.isArray(r.data) ? r.data : [];
      const match = lotes.find(l => (l.numero_lote || "").toUpperCase() === loteQuery.trim().toUpperCase()) || lotes[0];
      if (!match) {
        toast.error(`No se encontró un lote con número "${loteQuery}"`);
        setLoading(false);
        return;
      }
      setLoteId(match.id);
      const { data } = await api.get(`/inventory/lotes/${match.id}/auditoria-stock`);
      setResult(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const aplicarReparacion = async () => {
    if (!loteId) return;
    if (!window.confirm(
      `Reparar el stock del lote ${result?.numero_lote}?\n\n` +
      `Se cambiará cantidad_disponible de ${result?.cantidad_disponible_actual} a ${result?.cantidad_disponible_esperada} ` +
      `(diferencia: ${result?.diferencia}).\n\nEsta acción NO se puede deshacer automáticamente.`
    )) return;
    setAplicando(true);
    try {
      const { data } = await api.get(`/inventory/lotes/${loteId}/auditoria-stock?aplicar=true`);
      setResult(data);
      toast.success(`Stock reparado. Nuevo disponible: ${data.cantidad_disponible_esperada}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setAplicando(false); }
  };

  const escanearInconsistencias = async () => {
    setScanning(true);
    try {
      const { data } = await api.get("/inventory/lotes/auditoria-stock/inconsistencias?limit=50");
      setInconsistentes(data);
      if (data.total_inconsistentes_encontrados === 0) {
        toast.success(`✓ Sin inconsistencias detectadas en ${data.procesados} lotes activos`);
      } else {
        toast.warning(`${data.total_inconsistentes_encontrados} lote(s) con stock inconsistente encontrados`);
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setScanning(false); }
  };

  return (
    <div className="space-y-6" data-testid="auditoria-stock-panel">
      <div className="rounded-md border border-[#d1dbd6] bg-white p-5">
        <div className="flex items-start gap-3 mb-4">
          <ShieldCheck size={22} className="text-[#205c45] mt-0.5" />
          <div>
            <h2 className="text-lg font-semibold text-[#15241d]">Auditoría de stock por lote</h2>
            <p className="text-sm text-[#4a5c53]">
              Reconcilia el stock disponible de un lote con la suma de sus movimientos. Útil
              para detectar descuentos incorrectos tras splits FEFO, ediciones de factura o
              anulaciones que no se propagaron bien.
            </p>
          </div>
        </div>
        <div className="flex gap-2 items-end">
          <div className="flex-1">
            <Label>Número de lote</Label>
            <Input
              placeholder="Ej: 25A6C24AB"
              value={loteQuery}
              onChange={(e) => setLoteQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") buscarLote(); }}
              data-testid="auditoria-lote-input"
            />
          </div>
          <Button
            onClick={buscarLote}
            disabled={loading}
            className="bg-[#205c45] hover:bg-[#1a4a37]"
            data-testid="auditoria-buscar-btn"
          >
            {loading ? "Buscando…" : "Auditar lote"}
          </Button>
        </div>
      </div>

      {result && (
        <div className="rounded-md border border-[#d1dbd6] bg-white p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-xs uppercase tracking-wider text-[#5c7368]">Lote</div>
              <div className="text-lg font-semibold font-mono">{result.numero_lote}</div>
              <div className="text-sm text-[#4a5c53]">{result.producto_nombre}</div>
            </div>
            {result.consistente ? (
              <div className="flex items-center gap-2 text-[#205c45]">
                <CheckCircle2 size={20} /> <span className="font-semibold">Stock consistente</span>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-[#a35400]">
                <AlertTriangle size={20} /> <span className="font-semibold">Discrepancia detectada</span>
              </div>
            )}
          </div>
          <div className="grid grid-cols-4 gap-3 text-sm">
            <div className="rounded border p-3">
              <div className="text-xs text-[#5c7368] uppercase">Inicial</div>
              <div className="text-lg font-semibold">{result.cantidad_inicial}</div>
            </div>
            <div className="rounded border p-3">
              <div className="text-xs text-[#5c7368] uppercase">Suma movimientos</div>
              <div className={`text-lg font-semibold ${result.sum_movimientos < 0 ? "text-[#b53a25]" : "text-[#205c45]"}`}>{result.sum_movimientos}</div>
            </div>
            <div className="rounded border p-3">
              <div className="text-xs text-[#5c7368] uppercase">Actual (BD)</div>
              <div className="text-lg font-semibold">{result.cantidad_disponible_actual}</div>
            </div>
            <div className={`rounded border p-3 ${!result.consistente ? "border-[#e8b98a] bg-[#fef4e6]" : "border-[#b2d0c2] bg-[#e9f2ee]"}`}>
              <div className="text-xs text-[#5c7368] uppercase">Esperado</div>
              <div className="text-lg font-semibold">{result.cantidad_disponible_esperada}</div>
              {!result.consistente && (
                <div className="text-xs text-[#a35400]">Δ {result.diferencia}</div>
              )}
            </div>
          </div>
          {!result.consistente && !result.aplicado && (
            <div className="mt-4">
              <Button
                onClick={aplicarReparacion}
                disabled={aplicando}
                className="bg-[#a35400] hover:bg-[#7a3f00] text-white"
                data-testid="auditoria-reparar-btn"
              >
                <RefreshCcw size={14} className={`mr-2 ${aplicando ? "animate-spin" : ""}`} />
                {aplicando ? "Reparando…" : `Reparar stock (aplicar ${result.diferencia})`}
              </Button>
              <div className="text-xs text-[#5c7368] mt-2">
                Actualiza <code>cantidad_disponible</code> del lote a {result.cantidad_disponible_esperada}. Solo se corrige el saldo, los movimientos históricos NO se tocan.
              </div>
            </div>
          )}
          {result.movimientos_count > 0 && (
            <details className="mt-4">
              <summary className="cursor-pointer text-sm text-[#205c45]">Ver últimos {Math.min(20, result.movimientos_count)} movimientos ({result.movimientos_count} totales)</summary>
              <table className="w-full mt-2 text-xs">
                <thead className="text-[#5c7368]"><tr><th className="text-left">Fecha</th><th className="text-left">Tipo</th><th className="text-right">Cantidad</th><th className="text-left">Documento</th></tr></thead>
                <tbody>
                  {(result.movimientos || []).map(m => (
                    <tr key={m.id} className="border-t">
                      <td>{(m.created_at || m.fecha || "").slice(0, 19).replace("T", " ")}</td>
                      <td>{m.tipo}</td>
                      <td className={`text-right tabular-nums ${m.cantidad < 0 ? "text-[#b53a25]" : "text-[#205c45]"}`}>{m.cantidad}</td>
                      <td className="font-mono text-[10px]">{m.documento_tipo} #{m.documento_numero || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          )}
        </div>
      )}

      <div className="rounded-md border border-[#d1dbd6] bg-white p-5">
        <div className="flex items-start gap-3 mb-3">
          <AlertTriangle size={20} className="text-[#a35400] mt-0.5" />
          <div className="flex-1">
            <h3 className="font-semibold text-[#15241d]">Escaneo global de inconsistencias</h3>
            <p className="text-xs text-[#4a5c53]">Recorre todos los lotes activos y reporta los que tengan discrepancia. Puede tardar según el volumen.</p>
          </div>
          <Button
            variant="outline"
            onClick={escanearInconsistencias}
            disabled={scanning}
            data-testid="auditoria-scan-btn"
          >
            <RefreshCcw size={14} className={`mr-2 ${scanning ? "animate-spin" : ""}`} />
            {scanning ? "Escaneando…" : "Escanear todos"}
          </Button>
        </div>
        {inconsistentes && (
          <div className="text-sm">
            <div className="mb-2 text-[#4a5c53]">
              Procesados: <strong>{inconsistentes.procesados}</strong> · Inconsistentes: <strong className={inconsistentes.total_inconsistentes_encontrados > 0 ? "text-[#b53a25]" : "text-[#205c45]"}>{inconsistentes.total_inconsistentes_encontrados}</strong>
            </div>
            {inconsistentes.inconsistentes.length > 0 && (
              <table className="w-full text-xs">
                <thead className="text-[#5c7368]"><tr><th className="text-left">Lote</th><th className="text-left">Producto</th><th className="text-right">Actual</th><th className="text-right">Esperado</th><th className="text-right">Δ</th><th></th></tr></thead>
                <tbody>
                  {inconsistentes.inconsistentes.map(it => (
                    <tr key={it.lote_id} className="border-t">
                      <td className="font-mono">{it.numero_lote}</td>
                      <td>{it.producto_nombre}</td>
                      <td className="text-right tabular-nums">{it.cantidad_disponible}</td>
                      <td className="text-right tabular-nums">{it.cantidad_disponible_esperada}</td>
                      <td className={`text-right tabular-nums font-semibold ${it.diferencia < 0 ? "text-[#b53a25]" : "text-[#a35400]"}`}>{it.diferencia}</td>
                      <td>
                        <Button size="sm" variant="ghost" onClick={() => { setLoteQuery(it.numero_lote); setLoteId(it.lote_id); api.get(`/inventory/lotes/${it.lote_id}/auditoria-stock`).then(r => setResult(r.data)).catch(() => {}); }}>
                          Ver
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
